import { useMediaQuery } from '@mantine/hooks';
import {
  EnrollmentActivityLogGroupDto,
  EnrollmentActivityLogGroupType,
  SubmissionType,
} from '@zorro/clients';
import { getFullName } from '@zorro/common-utils';
import { Accordion } from '@zorro/design-system/v2/Accordion';
import { Avatar, getInitials } from '@zorro/design-system/v2/Avatar';
import { List } from '@zorro/design-system/v2/List';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { ZorroIcon } from '@zorro/shared/assets';
import {
  formatDateTimeEnLocaleFromUtc,
  singleLineTruncatedTextWithEllipsis,
} from '@zorro/shared/formatters';
import { Box, Group, Stack, Text } from '@zorro/web/design-system';
import { useTextOverflow } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { ActivityLogBenefitTypeBadges } from './ActivityLogBenefitTypeBadges';

const DATE_COLUMN_WIDTH = '9rem';
const PERFORMER_COLUMN_WIDTH = '8rem';

type Props = {
  group: EnrollmentActivityLogGroupDto;
};

const getGroupLabel = ({ type, rows, label }: EnrollmentActivityLogGroupDto) =>
  $enum.mapValue(type).with<string>({
    [EnrollmentActivityLogGroupType.ELECTION]: `Election details updated (${rows.length})`,
    [EnrollmentActivityLogGroupType.PAYMENTS]: `Payments details updated (${rows.length})`,
    [EnrollmentActivityLogGroupType.CARRIER_APPLICATION]: `Carrier application details updated (${rows.length})`,
    [EnrollmentActivityLogGroupType.SINGLE]: label,
  });

const ActivityLogSummary = ({ group }: Props) => {
  const isBiggerThanXl = useMediaQuery(`(min-width: 120rem)`);
  const label = getGroupLabel(group);
  const { isTextOverflowing, textRef } =
    useTextOverflow<HTMLParagraphElement>(label);

  return (
    <Group
      gap="var(--space-24)"
      wrap="nowrap"
      align="center"
      style={{ minWidth: 0 }}
    >
      <Text
        size="sm"
        fw={400}
        c="var(--color-text-default-secondary)"
        miw={DATE_COLUMN_WIDTH}
      >
        {formatDateTimeEnLocaleFromUtc(group.createdAt)}
      </Text>
      <Group
        gap="var(--space-8)"
        wrap="nowrap"
        align="center"
        style={{ minWidth: 0 }}
      >
        <Tooltip label={label} position="top" disabled={!isTextOverflowing}>
          <Text
            ref={textRef}
            size="sm"
            fw={400}
            c="var(--color-text-default-primary)"
            maw={
              isBiggerThanXl
                ? '50rem'
                : { base: '12rem', lg: '10rem', xl: '25rem' }
            }
            style={singleLineTruncatedTextWithEllipsis}
          >
            {label}
          </Text>
        </Tooltip>
        <ActivityLogBenefitTypeBadges
          benefitTypes={group.benefitTypes}
          metadata={group.metadata}
        />
      </Group>
    </Group>
  );
};

const ActivityLogPerformer = ({ group }: Props) => {
  const isAutomatic = group.submissionType === SubmissionType.AUTOMATIC;
  const fullName = getFullName({
    firstName: group.performedByFirstName,
    lastName: group.performedByLastName,
  });

  return (
    <Group gap="var(--space-8)" wrap="nowrap" w={PERFORMER_COLUMN_WIDTH}>
      {!isAutomatic &&
        group.performedByFirstName &&
        group.performedByLastName && (
          <>
            <Avatar size="sm">
              {getInitials(
                group.performedByFirstName,
                group.performedByLastName
              )}
            </Avatar>
            <Tooltip label={group.performedByEmail} position="top">
              <Text
                size="sm"
                c="var(--color-text-default-secondary)"
                style={{
                  ...singleLineTruncatedTextWithEllipsis,
                  maxWidth: PERFORMER_COLUMN_WIDTH,
                }}
              >
                {fullName}
              </Text>
            </Tooltip>
          </>
        )}
      {isAutomatic && (
        <>
          <Avatar w="1.5rem" h="1.5rem">
            <ZorroIcon style={{ width: 12, height: 12 }} />
          </Avatar>
          <Text size="sm" c="var(--color-text-default-secondary)">
            By system
          </Text>
        </>
      )}
    </Group>
  );
};

export const EmployeeActivityLogRow = ({ group }: Props) => {
  const { rows } = group;
  const isExpandable = rows.length > 0;
  const isCollapsed = rows.length > 1;

  return (
    <Accordion.Item value={group.id}>
      <Accordion.Control
        isNonExpandable={!isExpandable}
        rightSection={
          <Box mr="var(--space-4)">
            <ActivityLogPerformer group={group} />
          </Box>
        }
      >
        <ActivityLogSummary group={group} />
      </Accordion.Control>

      <Accordion.Panel>
        <Group
          wrap="nowrap"
          gap="var(--space-24)"
          pb="var(--space-16)"
          pr={`calc(${PERFORMER_COLUMN_WIDTH} + 2rem)`}
        >
          <Box miw={DATE_COLUMN_WIDTH} />
          <Stack gap="var(--space-12)" miw={0}>
            {isCollapsed ? (
              <List size="sm" spacing="var(--space-12)">
                {rows.map((row) => (
                  <List.Item key={row}>
                    <Text
                      display="block"
                      size="sm"
                      c="var(--color-text-default-secondary)"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {row}
                    </Text>
                  </List.Item>
                ))}
              </List>
            ) : (
              rows.map((row) => (
                <Text
                  key={row}
                  size="sm"
                  c="var(--color-text-default-secondary)"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {row}
                </Text>
              ))
            )}
          </Stack>
        </Group>
      </Accordion.Panel>
    </Accordion.Item>
  );
};
